import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@core/api';

export const workflowsApi = createApi({
  reducerPath: 'workflowsApi',
  baseQuery,
  tagTypes: ['Workflows', 'Steps'],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    // Workflow endpoints
    getWorkflows: builder.query({
      query: ({ statusGroup } = {}) => {
        const params = statusGroup ? `?status_group=${statusGroup}` : '';
        return `/api/v1/onboarding/workflows/${params}`;
      },
      providesTags: ['Workflows'],
    }),
    getWorkflowCounts: builder.query({
      query: () => '/api/v1/onboarding/workflows/counts/',
      // Same 'Workflows' tag type so any workflow mutation refetches the counts
      providesTags: [{ type: 'Workflows', id: 'COUNTS' }],
    }),
    getWorkflow: builder.query({
      query: (id) => `/api/v1/onboarding/workflows/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Workflows', id }],
    }),
    createWorkflow: builder.mutation({
      query: (data) => ({
        url: '/api/v1/onboarding/workflows/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Workflows'],
    }),
    updateWorkflow: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/onboarding/workflows/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Workflows', id }],
    }),
    activateWorkflow: builder.mutation({
      query: (id) => ({
        url: `/api/v1/onboarding/workflows/${id}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: ['Workflows'],
    }),
    duplicateWorkflow: builder.mutation({
      query: (id) => ({
        url: `/api/v1/onboarding/workflows/${id}/duplicate/`,
        method: 'POST',
      }),
      invalidatesTags: ['Workflows'],
    }),
    deleteWorkflow: builder.mutation({
      query: (id) => ({
        url: `/api/v1/onboarding/workflows/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Workflows'],
    }),
    archiveWorkflow: builder.mutation({
      query: (id) => ({
        url: `/api/v1/onboarding/workflows/${id}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: ['Workflows'],
    }),

    // Step endpoints
    getSteps: builder.query({
      query: (workflowId) =>
        `/api/v1/onboarding/workflows/${workflowId}/steps/`,
      providesTags: (result, error, workflowId) => [
        { type: 'Steps', id: `workflow-${workflowId}` },
      ],
    }),
    createStep: builder.mutation({
      query: ({ workflowId, ...data }) => ({
        url: `/api/v1/onboarding/workflows/${workflowId}/steps/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { workflowId }) => [
        { type: 'Steps', id: `workflow-${workflowId}` },
        { type: 'Workflows', id: workflowId },
      ],
    }),
    updateStep: builder.mutation({
      query: ({ workflowId, stepId, ...data }) => ({
        url: `/api/v1/onboarding/workflows/${workflowId}/steps/${stepId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { workflowId }) => [
        { type: 'Steps', id: `workflow-${workflowId}` },
        { type: 'Workflows', id: workflowId },
      ],
    }),
    deleteStep: builder.mutation({
      query: ({ workflowId, stepId }) => ({
        url: `/api/v1/onboarding/workflows/${workflowId}/steps/${stepId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { workflowId }) => [
        { type: 'Steps', id: `workflow-${workflowId}` },
        { type: 'Workflows', id: workflowId },
      ],
    }),
    reorderSteps: builder.mutation({
      query: ({ workflowId, step_orders }) => ({
        url: `/api/v1/onboarding/workflows/${workflowId}/steps/reorder/`,
        method: 'POST',
        body: { step_orders },
      }),
      invalidatesTags: (result, error, { workflowId }) => [
        { type: 'Steps', id: `workflow-${workflowId}` },
        { type: 'Workflows', id: 'LIST' },
      ],
    }),
    validateStepConfig: builder.mutation({
      query: (data) => ({
        url: '/api/v1/onboarding/steps/validate_config/',
        method: 'POST',
        body: data,
      }),
    }),
    previewStepMessage: builder.mutation({
      query: ({ workflowId, stepId }) => ({
        url: `/api/v1/onboarding/workflows/${workflowId}/steps/${stepId}/preview_message/`,
        method: 'POST',
      }),
    }),
    getSignTemplateUploadUrl: builder.mutation({
      query: ({ workflowId, stepId, ...body }) => ({
        url: `/api/v1/onboarding/workflows/${workflowId}/steps/${stepId}/sign-template-upload-url/`,
        method: 'POST',
        body,
      }),
    }),
    getSignTemplateFields: builder.mutation({
      query: ({ workflowId, stepId, ...body }) => ({
        url: `/api/v1/onboarding/workflows/${workflowId}/steps/${stepId}/sign-template-fields/`,
        method: 'POST',
        body,
      }),
    }),

    // Custom data keys endpoint
    getCustomDataKeys: builder.query({
      query: () => '/api/v1/onboarding/steps/custom_data_keys/',
    }),

    // WhatsApp template endpoint
    getWhatsAppTemplate: builder.query({
      query: (templateName) => `/api/v1/onboarding/whatsapp-template/${templateName}/`,
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const {
  useGetWorkflowsQuery,
  useGetWorkflowCountsQuery,
  useGetWorkflowQuery,
  useCreateWorkflowMutation,
  useUpdateWorkflowMutation,
  useActivateWorkflowMutation,
  useDuplicateWorkflowMutation,
  useDeleteWorkflowMutation,
  useArchiveWorkflowMutation,
  useGetStepsQuery,
  useCreateStepMutation,
  useUpdateStepMutation,
  useDeleteStepMutation,
  useReorderStepsMutation,
  useValidateStepConfigMutation,
  useGetCustomDataKeysQuery,
  useGetWhatsAppTemplateQuery,
  usePreviewStepMessageMutation,
  useGetSignTemplateUploadUrlMutation,
  useGetSignTemplateFieldsMutation,
} = workflowsApi;
